
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useAuthMethods = () => {
  const signInWithEmail = async (email: string, password: string) => {
    try {
      if (import.meta.env.DEV) console.log('Attempting to sign in');
      // Clear all localStorage before login attempt to avoid stale state
      if (import.meta.env.DEV) console.log('Clearing localStorage before login attempt');
      Object.keys(localStorage).forEach(key => {
        if (key.includes('supabase') || key.includes('entries') || key.includes('active')) {
          localStorage.removeItem(key);
        }
      });
    
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      if (import.meta.env.DEV) console.log('Sign in successful');
      toast.success('Signed in successfully');
    } catch (error: any) {
      console.error('Error signing in:', error.message);
      toast.error(error.error_description || error.message || 'Failed to sign in');
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      if (import.meta.env.DEV) console.log('Attempting to sign up');
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin + '/auth/callback',
        }
      });

      if (error) throw error;
      
      // Check if we got a valid user
      if (!data?.user) {
        throw new Error('Sign up successful but no user returned');
      }
      
      if (import.meta.env.DEV) console.log('Sign up successful');
      toast.success('Account created! Check your email to confirm.');
      
      return data;
    } catch (error: any) {
      console.error('Error signing up:', error.message);
      toast.error(error.error_description || error.message || 'Failed to sign up');
      throw error;
    }
  };

  const signInWithMagicLink = async (email: string) => {
    try {
      if (import.meta.env.DEV) console.log('Attempting to send magic link');
      const { error } = await supabase.auth.signInWithOtp({
        email,
      });

      if (error) throw error;
      toast.success('Check your email for the magic link');
    } catch (error: any) {
      console.error('Error sending magic link:', error.message);
      toast.error(error.message || 'Failed to send magic link');
      throw error;
    }
  };

  
  const signOut = async () => {
    try {
      if (import.meta.env.DEV) console.log('Starting signout process...');
      
      // First clear localStorage to ensure no data persistence if supabase fails
      localStorage.clear();
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      if (import.meta.env.DEV) console.log('Successfully signed out');
      toast.success('Signed out successfully');
      return true;
    } catch (error: any) {
      console.error('Exception during sign out:', error.message);
      // Make sure localStorage is cleared even if supabase call fails
      localStorage.clear();
      toast.error(error.message || 'Error during sign out');
      return false;
    }
  };

  return {
    signInWithEmail,
    signUpWithEmail,
    signInWithMagicLink,
    signOut,
  };
};
